<template>
  <div class="statement-form">
    <v-card v-if="showConsentGate" class="statement-card" elevation="1">
      <div class="statement-header">
        <v-card-title class="statement-title">
          <span class="statement-title__institution">
            {{ institutionTitle }}
          </span>
          <span class="statement-title__form">
            Согласие на обработку персональных данных
          </span>
        </v-card-title>
      </div>

      <v-card-text class="statement-content">
        <div class="statement-consent" v-html="consentApplicantsText"></div>
      </v-card-text>

      <v-card-actions class="statement-actions statement-actions--end">
        <v-btn color="primary" variant="flat" @click="acceptConsent">
          Согласен
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-form autocomplete="one-time-code" v-else ref="statementForm" lazy-validation :disabled="isPublicApprovedApplication" :pa_id="formData.pa_id">
      <v-card class="statement-card" elevation="1">
        <div class="statement-header">
          <div>
            <v-card-title class="statement-title">
              <span class="statement-title__institution">
                {{ institutionTitle }}
              </span>
              <span class="statement-title__form">
                Заявление на поступление<span v-if="applicationId">: анкета №{{ applicationId }}</span>
              </span>
            </v-card-title>
          </div>

          <div class="statement-header__aside">
            <a v-if="showExistingApplicationLink" class="statement-existing-link" href="#"
              @click.prevent="openLoadApplicationDialog">
              Уже заполняли заявление?<br>
              Тогда вы можете его отредактировать
            </a>

            <v-chip v-if="offline" class="statement-offline-chip" color="error" variant="tonal" size="small">
              Офлайн
            </v-chip>
          </div>
        </div>

        <v-card-text class="statement-content">
          <v-alert v-if="admissionPeriodMessage" class="statement-period-alert" type="warning" variant="tonal">
            {{ admissionPeriodMessage }}
          </v-alert>

          <v-alert v-if="isPublicApprovedApplication" class="statement-period-alert" type="info" variant="tonal">
            Анкета уже утверждена приемной комиссией. Редактирование недоступно.
          </v-alert>

          <div v-if="ApplicationLoading" class="statement-loading">
            <v-skeleton-loader type="heading, paragraph, paragraph" />
            <v-skeleton-loader type="article" />
            <v-skeleton-loader type="article" />
            <v-skeleton-loader type="actions" />
          </div>

          <template v-else>
            <div v-if="!isAdmissionPeriodClosed" class="statement-layout">
              <nav class="statement-nav" aria-label="Разделы заявления">
                <button
                  v-for="item in navigationItems"
                  :key="item.id"
                  type="button"
                  class="statement-nav__link"
                  @click="scrollToSection(item.id)"
                >
                  <span class="statement-nav__index">{{ item.index }}</span>
                  <span class="statement-nav__text">{{ item.title }}</span>
                </button>

                <div v-if="$model.isAdmin && applicationId" class="statement-nav__admin">
                  <v-btn
                    block
                    size="small"
                    color="primary"
                    variant="tonal"
                    prepend-icon="mdi-history"
                    :loading="applicantLogLoading"
                    @click="openApplicantLogDialog"
                  >
                    Показать лог
                  </v-btn>
                </div>
              </nav>

              <div class="statement-sections">
                <div id="statement-personal" class="statement-anchor">
            <personal-section :form-data="formData" :birth-date-min="birthDateMin"
              :birth-date-max="birthDateMax" :birth-date-rules="birthDateRules" :required-name-rules="requiredNameRules"
              :optional-name-rules="optionalNameRules" :snils-rules="snilsRules" :email-rules="emailRules"
              :phone-rules="phoneRules" :snils-required="requiredFields.snils" :email-required="requiredFields.email"
              @update-name="updateApplicantName" @update-snils="updateSnils" @update-phone="updatePhone" />
                </div>

                <div id="statement-document" class="statement-anchor">
            <document-section :form-data="formData"
              :registration-address-required="requiredFields.registrationAddress" />
                </div>

                <div id="statement-payers" class="statement-anchor">
            <payers-section :form-data="formData" :birth-date-min="birthDateMin"
              :birth-date-max="birthDateMax" :optional-name-rules="optionalNameRules"
              :optional-snils-rules="optionalSnilsRules" :optional-email-rules="optionalEmailRules"
              :optional-phone-rules="optionalPhoneRules" @add-payer="addPayer" @remove-payer="removePayer"
              :payers-required="isPayersRequired" @update-payer-name="updatePayerName" @update-payer-snils="updatePayerSnils"
              @update-payer-phone="updatePayerPhone" />
                </div>

                <div id="statement-certificate" class="statement-anchor">
            <certificate-section :form-data="formData"
              :previous-education-levels="previousEducationLevels" :subjects-master="$model.subjects"
              :subjects-gia="$model.subjectsGIA" :show-gia-results="showGiaResults"
              :graduation-year-rules="graduationYearRules" :document-date-min="documentDateMin"
              :document-date-max="documentDateMax" :document-date-rules="documentDateRules"
              :subject-grade-rules="subjectGradeRules" :gia-result-rules="giaResultRules"
              :subject-grade-average="subjectGradeAverage" :graduation-year-required="requiredFields.graduationYear"
              :certificate-received-date-required="requiredFields.certificateReceivedDate"
              :education-document-number-required="requiredFields.educationDocumentNumber"
              :certificate-grades-required="requiredFields.certificateGrades"
              :has-filled-certificate-grades="hasFilledCertificateGrades"
              :gia-results-required="isGiaResultsRequired" :certificate-average-rules="certificateAverageRules"
              @normalize-graduation-year="normalizeGraduationYear" @update-subject-grade="updateSubjectGrade"
              @update-gia-result="updateGiaResult" @update-certificate-average="updateCertificateAverage"
              @normalize-certificate-average="normalizeCertificateAverage" />
                </div>

                <div id="statement-additional" class="statement-anchor">
            <additional-section :form-data="formData"
              :dormitory-need-options="dormitoryNeedOptions" />
                </div>

                <div id="statement-contests" class="statement-anchor">
            <competitive-groups-section :form-data="formData"
              :grouped-competitive-groups="groupedCompetitiveGroups"
              :selected-specialty-form-keys="selectedSpecialtyFormKeys" :priority-options="priorityOptions"
              :selected-contest-details="selectedContestDetails" :get-selected-specialty-form="getSelectedSpecialtyForm"
              :open-selected-panels-token="openSelectedPanelsToken" :set-selected-specialty-form="setSelectedSpecialtyForm" :get-form-funding-options="getFormFundingOptions"
              :get-contest-options-for-funding="getContestOptionsForFunding" :is-contest-selected="isContestSelected"
              :is-contest-option-disabled="isContestOptionDisabled" :toggle-contest-selection="toggleContestSelection"
              :get-contest-option-label="getContestOptionLabel"
              :get-group-qualification-label="getGroupQualificationLabel" :get-department-name="getDepartmentName"
              :get-funding-label="getFundingLabel" :get-education-base-label="getEducationBaseLabel" :update-contest-priority="updateContestPriority"
              :remove-contest="removeContest" />
                </div>

                <div v-if="showDocumentsSection" id="statement-documents" class="statement-anchor">
            <documents-section
              :form-data="formData"
              :application-id="applicationId"
              :edit-password="applicationEditPassword"
              :is-staff="$model.isStaff"
              :documents="$model.received_documents"
              :scan-documents="$model.received_documents_scan"
              :upload-max-size="$model.uploadMaxSize"
              :available-upload-extensions="$model.availableUploadExtensions"
            />
                </div>

                <div v-if="$model.isStaff && formData.approved" id="statement-print" class="statement-anchor">
            <print-documents-section
              :form-data="formData"
              :selected-contest-details="selectedContestDetails"
              :hostel-list="$model.hostelList || []"
              :contract-templates="$model.contractTemplates || []"
              :services="$model.services || []"
              :get-department-name="getDepartmentName"
              :get-funding-label="getFundingLabel"
              :get-group-qualification-label="getGroupQualificationLabel"
            />
                </div>

            <v-alert v-if="formIssueList.length" class="statement-validation-summary"
              type="info" variant="tonal">
              <div class="statement-validation-summary__title">
                Проверьте поля перед сохранением
              </div>
              <ul class="statement-validation-summary__list">
                <li v-for="issue in formIssueList" :key="issue">
                  {{ issue }}
                </li>
              </ul>
            </v-alert>
              </div>
            </div>
          </template>
        </v-card-text>

        <v-card-actions v-if="!isAdmissionPeriodClosed && !ApplicationLoading"
          class="statement-actions statement-actions--form">
          <div v-if="offline" class="statement-offline-note">
            Вы офлайн. Сохранение анкеты требует подключения к сети.
          </div>

          <div v-else-if="ApplicationErrors.length || ApplicationErrorLinks.length" class="statement-Application-errors">
            <div v-for="error in ApplicationErrors" :key="error">
              {{ error }}
            </div>
            <div v-for="link in ApplicationErrorLinks" :key="link.url">
              <a :href="link.url" target="_blank" rel="noopener">
                {{ link.text }}
              </a>
            </div>
          </div>

          <v-alert v-else-if="ApplicationWarnings.length" type="info" variant="tonal" class="statement-application-warnings">
            <div v-for="warning in ApplicationWarnings" :key="warning">
              {{ warning }}
            </div>
          </v-alert>

          <div class="statement-actions__buttons">
            <v-btn :color="ApplicationSaving ? 'grey-darken-1' : 'primary'" variant="flat"
              prepend-icon="mdi-content-save" :loading="ApplicationSaving" :disabled="ApplicationSaving || isPublicApprovedApplication"
              @click="submitApplication">
              {{ ApplicationSaving ? 'Сохраняем анкету' : 'Сохранить анкету' }}
            </v-btn>

            <v-btn v-if="$model.isStaff" :color="formData.approved ? 'error' : 'success'" variant="flat"
              :prepend-icon="formData.approved ? 'mdi-close-circle' : 'mdi-check-circle'" :loading="approvalSaving"
              :disabled="ApplicationSaving || approvalSaving || originalsSaving || !applicationId" @click="toggleApplicationApproval">
              {{ formData.approved ? 'Исключить из конкурса' : 'Утвердить заявление' }}
            </v-btn>

            <v-btn v-if="$model.isStaff" :color="formData.originalsAccepted ? 'error' : 'success'" variant="flat"
              :prepend-icon="formData.originalsAccepted ? 'mdi-file-remove' : 'mdi-file-check'"
              :loading="originalsSaving"
              :disabled="ApplicationSaving || approvalSaving || originalsSaving || !applicationId"
              @click="toggleApplicationOriginals">
              {{ formData.originalsAccepted ? 'Отменить принятие оригиналов' : 'Оригиналы приняты' }}
            </v-btn>

          </div>
        </v-card-actions>
      </v-card>
    </v-form>

    <v-dialog v-model="passwordDialog" max-width="460">
      <v-card>
        <v-card-title>Пароль для редактирования анкеты</v-card-title>
        <v-card-text>
          Сохраните этот пароль. Он понадобится, чтобы позже открыть анкету для редактирования.
          <div class="statement-password">
            {{ applicationPassword }}
          </div>
          <div
            v-if="$model.settings && $model.settings.additionalInfoForApplicant"
            class="statement-save-information"
            v-html="$model.settings.additionalInfoForApplicant"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" variant="flat" @click="passwordDialog = false">
            Понятно
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="loadApplicationDialog" max-width="460">
      <v-card>
        <v-card-title>Редактирование заявления</v-card-title>
        <v-card-text>
          <v-text-field autocomplete="one-time-code" v-model="loadApplicationPassword" label="Пароль анкеты" type="password" variant="outlined"
            density="comfortable" autofocus :error-messages="loadApplicationErrors"
            @keyup.enter="loadApplicationByPassword" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="loadApplicationDialog = false">
            Отмена
          </v-btn>
          <v-btn color="primary" variant="flat" :loading="ApplicationLoading" @click="loadApplicationByPassword">
            Открыть
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <applicant-log-dialog
      v-if="$model.isAdmin"
      v-model="applicantLogDialog"
      :application-id="applicationId"
      title="Лог анкеты"
      @update:loading="applicantLogLoading = $event"
    />
  </div>
</template>

<script>
import { isSnilsValid } from 'moodleDir/procollege/lib/vue/helpers.js'
import {
  defaultForm,
  defaultPayer,
  normalizeAcceptedDocuments
} from './defaultApplicantForm.js'
import {
  addYears,
  formatDateForInput,
  formatUnixDateTime,
  formatSnils,
  formatPhone,
  normalizeDigits,
  normalizePersonName,
  isPersonNameValid,
  isEmailValid,
  isPhoneComplete,
  isBlank
} from './applicantFormatters.js'
import {
  addRequiredIssue,
  addNameIssue,
  hasAddressData,
  addDateIssue,
  addGraduationYearIssue,
  hasFilledPayer,
  getDictionaryName,
  collectFieldLengthIssues
} from './applicantValidation.js'
import PersonalSection from './sections/PersonalSection.vue'
import DocumentSection from './sections/DocumentSection.vue'
import PayersSection from './sections/PayersSection.vue'
import CertificateSection from './sections/CertificateSection.vue'
import AdditionalSection from './sections/AdditionalSection.vue'
import CompetitiveGroupsSection from './sections/CompetitiveGroupsSection.vue'
import DocumentsSection from './sections/DocumentsSection.vue'
import PrintDocumentsSection from './sections/PrintDocumentsSection.vue'
import ApplicantLogDialog from '../../components/ApplicantLogDialog.vue'

export default {
  name: 'Statement',
  components: {
    PersonalSection,
    DocumentSection,
    PayersSection,
    CertificateSection,
    AdditionalSection,
    CompetitiveGroupsSection,
    DocumentsSection,
    PrintDocumentsSection,
    ApplicantLogDialog
  },
  data() {
    const routeApplicationId = String(this.$route.params.id || '').replace(/^\/+/, '') || null

    return {
      offline: !navigator.onLine,
      routeApplicationId,
      applicationId: this.$model.isStaff ? routeApplicationId : null,
      applicationAccessGranted: this.$model.isStaff,
      ApplicationLoading: false,
      ApplicationSaving: false,
      approvalSaving: false,
      originalsSaving: false,
      isApplyingLoadedApplication: false,
      ApplicationErrors: [],
      ApplicationErrorLinks: [],
      ApplicationWarnings: [],
      applicationPassword: null,
      applicationEditPassword: '',
      passwordDialog: false,
      loadApplicationDialog: false,
      loadApplicationPassword: '',
      loadApplicationErrors: [],
      applicantLogDialog: false,
      applicantLogLoading: false,
      consentAccepted: this.$model.isStaff,
      requiredHighlightFrame: null,
      formData: defaultForm(),
      dormitoryNeedOptions: [
        { value: 'yes', label: 'да' },
        { value: 'no', label: 'нет' },
        { value: 'session', label: 'на период сессии' }
      ],
      previousEducationLevels: [
        { key: 0, value: 'нет' },
        { key: 1, value: 'НПO(училище)' },
        { key: 2, value: 'СПO (по программам подготовки квалифицированных рабочих, служащих)' },
        { key: 3, value: 'ВУЗ' },
        { key: 4, value: 'СПO (по программам подготовки специалистов среднего звена)' },
      ],
      selectedSpecialtyFormKeys: {},
      openSelectedPanelsToken: 0
    }
  },
  computed: {
    /**
     * Shows password-based editing only before public access is granted.
     */
    showExistingApplicationLink() {
      return !this.$model.isStaff && !this.applicationAccessGranted
    },
    consentApplicantsText() {
      return this.$model && this.$model.consentApplicants
        ? this.$model.consentApplicants
        : ''
    },
    /**
     * Blocks public users until they accept personal data consent.
     */
    showConsentGate() {
      return !this.$model.isStaff &&
        !this.consentAccepted &&
        Boolean(this.consentApplicantsText)
    },
    /**
     * Public applicants can view approved applications by password, but cannot edit them.
     */
    isPublicApprovedApplication() {
      return !this.$model.isStaff && Boolean(this.formData.approved)
    },
    navigationItems() {
      const items = [
        { id: 'statement-personal', index: 1, title: 'Личные данные' },
        { id: 'statement-document', index: 2, title: 'Документ и адрес' },
        { id: 'statement-payers', index: 3, title: 'Плательщики' },
        { id: 'statement-certificate', index: 4, title: 'Аттестат' },
        { id: 'statement-additional', index: 5, title: 'Дополнительно' },
        { id: 'statement-contests', index: 6, title: 'Конкурсы' }
      ]

      if (this.showDocumentsSection) {
        items.push({ id: 'statement-documents', index: 7, title: 'Документы' })
      }

      if (this.$model.isStaff && this.formData.approved) {
        items.push({ id: 'statement-print', index: 8, title: 'Печать' })
      }

      return items
    },
    showDocumentsSection() {
      return Boolean(this.applicationId) && (
        this.$model.isStaff ||
        this.applicationAccessGranted
      )
    },
    /**
     * Merges backend-required field flags with local defaults for older pages.
     */
    requiredFields() {
      const defaults = {
        payersForMinors: true,
        payersForAll: false,
        snils: true,
        email: true,
        graduationYear: true,
        certificateReceivedDate: true,
        educationDocumentNumber: true,
        giaResults: this.showGiaResults,
        certificateGrades: true,
        registrationAddress: true
      }
      const configured = (this.$model && this.$model.requiredFields) || {}
      const normalized = {}

      Object.keys(defaults).forEach(key => {
        const value = Object.prototype.hasOwnProperty.call(configured, key)
          ? configured[key]
          : defaults[key]

        normalized[key] = value === true || value === 1 || value === '1'
      })

      return normalized
    },
    isApplicantMinor() {
      if (!this.formData.birthDate) {
        return false
      }

      const birthDate = new Date(`${this.formData.birthDate}T00:00:00`)
      if (Number.isNaN(birthDate.getTime())) {
        return false
      }

      const adultDate = this.addYears(birthDate, 18)

      return adultDate > new Date()
    },
    isPayersRequired() {
      return Boolean(this.requiredFields.payersForAll) ||
        (Boolean(this.requiredFields.payersForMinors) && this.isApplicantMinor)
    },
    isGiaResultsRequired() {
      return this.showGiaResults && Boolean(this.requiredFields.giaResults)
    },
    admissionPeriod() {
      return this.$model && this.$model.admissionPeriod
        ? this.$model.admissionPeriod
        : {}
    },
    admissionPeriodNow() {
      return Math.floor(Date.now() / 1000)
    },
    isAdmissionPeriodNotStarted() {
      const startDate = Number(this.admissionPeriod.startDate || 0)

      return startDate > 0 && this.admissionPeriodNow < startDate
    },
    isAdmissionPeriodFinished() {
      const endDate = Number(this.admissionPeriod.endDate || 0)

      return endDate > 0 && this.admissionPeriodNow > endDate
    },
    /**
     * Indicates that the configured admission period blocks form actions.
     */
    isAdmissionPeriodClosed() {
      return !this.$model.isStaff &&
        (this.isAdmissionPeriodNotStarted || this.isAdmissionPeriodFinished)
    },
    /**
     * Builds a user-facing admission period message with exact dates.
     */
    admissionPeriodMessage() {
      if (this.isAdmissionPeriodNotStarted) {
        return `Прием заявлений еще не начался${this.admissionPeriod.startDate
          ? `. Начало приема: ${this.formatUnixDateTime(this.admissionPeriod.startDate)}`
          : ''}.`
      }

      if (this.isAdmissionPeriodFinished) {
        return `Прием заявлений уже завершился${this.admissionPeriod.endDate
          ? `. Окончание приема: ${this.formatUnixDateTime(this.admissionPeriod.endDate)}`
          : ''}.`
      }

      return ''
    },
    /**
     * Uses institution short name when present, otherwise full name.
     */
    institutionTitle() {
      const dataInst = this.$model && this.$model.dataInst
        ? this.$model.dataInst
        : {}
      const shortname = (dataInst.shortname || '').trim()

      return shortname || dataInst.name || ''
    },
    birthDateMin() {
      return this.formatDateForInput(this.addYears(new Date(), -70))
    },
    birthDateMax() {
      return this.formatDateForInput(this.addYears(new Date(), -14))
    },
    birthDateRules() {
      return [
        value => Boolean(value) || 'Укажите дату рождения',
        value => !value || value >= this.birthDateMin || 'Возраст поступающего не должен превышать 70 лет',
        value => !value || value <= this.birthDateMax || 'Поступающему должно быть не менее 14 лет'
      ]
    },
    requiredNameRules() {
      return [
        value => Boolean(String(value || '').trim()) || 'Заполните поле',
        value => this.isPersonNameValid(value) || 'Допустимы кириллица, пробел, дефис и апостроф'
      ]
    },
    optionalNameRules() {
      return [
        value => !value || this.isPersonNameValid(value) || 'Допустимы кириллица, пробел, дефис и апостроф'
      ]
    },
    documentDateMin() {
      return this.formatDateForInput(this.addYears(new Date(), -100))
    },
    documentDateMax() {
      return this.formatDateForInput(new Date())
    },
    documentDateRules() {
      return [
        value => !this.requiredFields.certificateReceivedDate || Boolean(value) || 'Заполните поле',
        value => !value || value >= this.documentDateMin || 'Дата не должна быть старше 100 лет',
        value => !value || value <= this.documentDateMax || 'Дата не может быть позже текущей даты'
      ]
    },
    graduationYearRules() {
      const minYear = this.addYears(new Date(), -100).getFullYear()
      const maxYear = new Date().getFullYear()

      return [
        value => !this.requiredFields.graduationYear || Boolean(value) || 'Укажите год окончания',
        value => !value || /^\d{4}$/.test(String(value || '')) || 'Введите год в формате ГГГГ',
        value => !value || Number(value) >= minYear || 'Год не должен быть старше 100 лет',
        value => !value || Number(value) <= maxYear || 'Год не может быть позже текущего'
      ]
    },
    snilsRules() {
      return [
        value => !this.requiredFields.snils || Boolean(value) || 'Укажите СНИЛС',
        value => !value || isSnilsValid(value) || 'Введите корректный СНИЛС'
      ]
    },
    optionalSnilsRules() {
      return [
        value => !value || isSnilsValid(value) || 'Введите корректный СНИЛС'
      ]
    },
    emailRules() {
      return [
        value => !this.requiredFields.email || Boolean(value) || 'Укажите email',
        value => !value || this.isEmailValid(value) || 'Введите корректный email'
      ]
    },
    optionalEmailRules() {
      return [
        value => !value || this.isEmailValid(value) || 'Введите корректный email'
      ]
    },
    phoneRules() {
      return [
        value => this.isPhoneComplete(value) || 'Укажите телефон полностью'
      ]
    },
    optionalPhoneRules() {
      return [
        value => !value || value === '+7' || this.isPhoneComplete(value) || 'Укажите телефон полностью'
      ]
    },
    subjectGradeRules() {
      return [
        value => !this.requiredFields.certificateGrades || Boolean(value) || 'Укажите оценку',
        value => !value || /^\d+$/.test(String(value || '')) || 'Только цифры',
        value => !value || Number(value) >= 1 || 'Оценка должна быть не меньше 1',
        value => !value || Number(value) <= 5 || 'Оценка должна быть не больше 5'
      ]
    },
    giaResultRules() {
      return [
        value => !this.isGiaResultsRequired || Boolean(value) || 'Укажите результат',
        value => !value || /^\d+$/.test(String(value || '')) || 'Только цифры'
      ]
    },
    certificateAverageRules() {
      return [
        value => !value || /^\d+([.,]\d{1,2})?$/.test(String(value || '')) || 'Не больше 2 знаков после запятой',
        value => !value || Number(String(value).replace(',', '.')) <= 5 || 'Средний балл должен быть не больше 5',
        value => !value || Number(String(value).replace(',', '.')) >= 0 || 'Средний балл не должен быть отрицательным'
      ]
    },
    /**
     * Calculates the certificate average displayed in the form.
     */
    subjectGradeAverage() {
      const grades = this.formData.selectedSubjects
        .map(subject => this.formData.subjectGrades[subject])
        .filter(value => !this.isBlank(value))
        .map(value => Number(value))
        .filter(grade => Number.isFinite(grade))

      if (!grades.length) {
        return '0.00'
      }

      const sum = grades.reduce((total, grade) => total + grade, 0)

      return (sum / grades.length).toFixed(2)
    },
    hasFilledCertificateGrades() {
      return this.formData.selectedSubjects.some(subject => (
        !this.isBlank(this.formData.subjectGrades[subject])
      ))
    },
    showGiaResults() {
      return String(this.$model && this.$model.settings ? this.$model.settings.gia : '') === '1'
    },
    maxContestCount() {
      const count = this.$model && this.$model.settings
        ? Number(this.$model.settings.countPriority)
        : 0

      return count > 0 ? count : 5
    },
    priorityOptions() {
      return Array.from(
        { length: this.maxContestCount },
        (_, index) => index + 1
      )
    },
    /**
     * Flattens and de-duplicates all grouped competitive groups from the model.
     */
    allCompetitiveGroups() {
      const groups = this.$model && this.$model.competitiveGroups
        ? this.$model.competitiveGroups
        : {}

      return Object.values(groups)
        .flatMap(groupSet => Object.values(groupSet || {}))
        .filter((group, index, list) => (
          list.findIndex(item => String(item.id) === String(group.id)) === index
        ))
    },
    /**
     * Applies admission filters: education base, replenishment and target.
     */
    filteredCompetitiveGroups() {
      return this.allCompetitiveGroups.filter(group => {
        if (String(group.base_education) !== String(this.formData.educationBase)) {
          return false
        }

        if (String(group.in_group) !== (this.formData.isGroupReplenishment ? '1' : '0')) {
          return false
        }

        if (this.formData.targetOrganizationId && !this.groupHasTarget(group, this.formData.targetOrganizationId)) {
          return false
        }

        return this.getFundingOptions(group).length > 0
      })
    },
    /**
     * Groups competitive groups by department and specialty for step-by-step UI.
     */
    groupedCompetitiveGroups() {
      const departments = new Map()

      this.filteredCompetitiveGroups.forEach(group => {
        const departmentId = String(group.department_id || 0)
        if (!departments.has(departmentId)) {
          departments.set(departmentId, {
            id: departmentId,
            name: this.getDepartmentName(departmentId),
            groups: [],
            specialtyMap: new Map()
          })
        }

        const department = departments.get(departmentId)
        const specialtyKey = this.getSpecialtyKey(group)

        department.groups.push(group)
        if (!department.specialtyMap.has(specialtyKey)) {
          department.specialtyMap.set(specialtyKey, {
            key: specialtyKey,
            name: group.name,
            code: group.code,
            groups: []
          })
        }

        department.specialtyMap.get(specialtyKey).groups.push(group)
      })

      return Array.from(departments.values()).map(department => ({
        ...department,
        specialties: Array.from(department.specialtyMap.values()).map(specialty => ({
          ...specialty,
          formOptions: this.getSpecialtyFormOptions(specialty.groups),
          groups: specialty.groups
        }))
      }))
    },
    /**
     * Restores saved contest selections with full group data for display.
     */
    selectedContestDetails() {
      return this.formData.selectedContests
        .map(contest => {
          const group = this.allCompetitiveGroups.find(
            item => String(item.id) === String(contest.groupId)
          )

          return group
            ? {
              ...contest,
              educationBase: contest.educationBase || group.base_education,
              group
            }
            : null
        })
        .filter(Boolean)
    },
    /**
     * Collects missing or invalid fields into one applicant-facing checklist.
     */
    formIssueList() {
      const issues = []
      const passport = this.formData.passport || {}

      this.addNameIssue(issues, this.formData.lastname, 'Личные данные: фамилия', true)
      this.addNameIssue(issues, this.formData.firstname, 'Личные данные: имя', true)
      this.addNameIssue(issues, this.formData.patronymic, 'Личные данные: отчество', false)
      this.addRequiredIssue(issues, this.formData.gender, 'Личные данные: пол')

      if (this.requiredFields.snils && this.isBlank(this.formData.snils)) {
        issues.push('Личные данные: СНИЛС')
      } else if (!this.isBlank(this.formData.snils) && !isSnilsValid(this.formData.snils)) {
        issues.push('Личные данные: СНИЛС заполнен некорректно')
      }

      this.addDateIssue(issues, this.formData.birthDate, 'Личные данные: дата рождения', this.birthDateMin, this.birthDateMax)

      if (this.requiredFields.email && this.isBlank(this.formData.email)) {
        issues.push('Личные данные: email')
      } else if (!this.isBlank(this.formData.email) && !this.isEmailValid(this.formData.email)) {
        issues.push('Личные данные: email заполнен некорректно')
      }

      if (!this.isPhoneComplete(this.formData.phone)) {
        issues.push('Личные данные: телефон заполнен не полностью')
      }

      this.addRequiredIssue(issues, this.formData.citizenshipType, 'Личные данные: тип гражданства')
      this.addRequiredIssue(issues, this.formData.citizenship, 'Личные данные: гражданство')
      this.addRequiredIssue(issues, this.formData.birthPlace, 'Личные данные: место рождения')

      this.addRequiredIssue(issues, passport.documentType, 'Документ: тип документа')
      this.addRequiredIssue(issues, passport.series, 'Документ: серия')
      this.addRequiredIssue(issues, passport.number, 'Документ: номер')
      this.addDateIssue(issues, passport.issueDate, 'Документ: дата выдачи', this.documentDateMin, this.documentDateMax)
      if (String(passport.documentType) === '21') {
        if (this.isBlank(passport.subdivisionCode)) {
          issues.push('Документ: код подразделения')
        } else if (!/^\d{3}-\d{3}$/.test(String(passport.subdivisionCode))) {
          issues.push('Документ: код подразделения должен быть в формате 000-000')
        }
      }
      this.addRequiredIssue(issues, passport.issuedBy, 'Документ: кем выдан')

      const registrationAddress = this.formData.registrationAddress || {}
      if (this.requiredFields.registrationAddress && !this.hasAddressData(registrationAddress)) {
        issues.push('Документ и адрес: адрес регистрации нужно выбрать из подсказки или заполнить вручную')
      }

      this.addRequiredIssue(issues, this.formData.educationBase, 'Аттестат: база образования')
      this.addGraduationYearIssue(issues, this.formData.graduationYear, this.requiredFields.graduationYear)
      this.addDateIssue(issues, this.formData.certificateReceivedDate, 'Аттестат: дата получения аттестата', this.documentDateMin, this.documentDateMax, this.requiredFields.certificateReceivedDate)
      this.addRequiredIssue(issues, this.formData.educationDocumentNumber, 'Аттестат: номер аттестата/свидетельства', this.requiredFields.educationDocumentNumber)

      if (this.requiredFields.certificateGrades && !this.formData.selectedSubjects.length) {
        issues.push('Аттестат: предметы аттестата')
      } else {
        this.formData.selectedSubjects.forEach(subject => {
          const grade = this.formData.subjectGrades[subject]
          const subjectName = this.getSubjectName(subject)

          if (this.requiredFields.certificateGrades && this.isBlank(grade)) {
            issues.push(`Аттестат: оценка по предмету "${subjectName}"`)
          } else if (!this.isBlank(grade) && !/^\d+$/.test(String(grade))) {
            issues.push(`Аттестат: оценка по предмету "${subjectName}" должна содержать только цифры`)
          } else if (!this.isBlank(grade) && Number(grade) < 1) {
            issues.push(`Аттестат: оценка по предмету "${subjectName}" должна быть не меньше 1`)
          } else if (!this.isBlank(grade) && Number(grade) > 5) {
            issues.push(`Аттестат: оценка по предмету "${subjectName}" должна быть не больше 5`)
          }
        })
      }

      if (!this.requiredFields.certificateGrades &&
        !this.hasFilledCertificateGrades &&
        !this.isBlank(this.formData.certificateAverage)) {
        const average = String(this.formData.certificateAverage).replace(',', '.')

        if (!/^\d+(\.\d{1,2})?$/.test(average)) {
          issues.push('Аттестат: средний балл должен быть указан с точностью до 2 знаков после запятой')
        } else if (Number(average) > 5) {
          issues.push('Аттестат: средний балл должен быть не больше 5')
        } else if (Number(average) < 0) {
          issues.push('Аттестат: средний балл не должен быть отрицательным')
        }
      }

      if (this.showGiaResults) {
        if (this.isGiaResultsRequired && !this.formData.selectedGiaSubjects.length) {
          issues.push('Аттестат: результаты ГИА')
        } else {
          this.formData.selectedGiaSubjects.forEach(subject => {
            const result = this.formData.giaResults[subject]
            const subjectName = this.getGiaSubjectName(subject)

            if (this.isGiaResultsRequired && this.isBlank(result)) {
              issues.push(`Аттестат: результат ГИА по предмету "${subjectName}"`)
            } else if (!this.isBlank(result) && !/^\d+$/.test(String(result))) {
              issues.push(`Аттестат: результат ГИА по предмету "${subjectName}" должен содержать только цифры`)
            }
          })
        }
      }

      if (this.isPayersRequired && !hasFilledPayer(this.formData.payers)) {
        issues.push('Сведения о плательщиках: заполните данные родителя или представителя')
      }

      if (!this.formData.selectedContests.length) {
        issues.push('Конкурсные группы: выберите хотя бы один конкурс')
      }

      return [
        ...issues,
        ...collectFieldLengthIssues(this.formData, this.$model.fieldLimits || {})
      ]
    }
  },

  watch: {
    formData: {
      handler() {
        this.scheduleRequiredHighlights()
      },
      deep: true
    },
    'formData.isGroupReplenishment'() {
      if (this.isApplyingLoadedApplication) {
        return
      }

      this.clearContestSelection()
    },
    'formData.targetOrganizationId'() {
      if (this.isApplyingLoadedApplication) {
        return
      }

      this.clearContestSelection()
    },
    'formData.submittedViaEpgu'(value) {
      if (!value) {
        this.formData.eserviceNumber = ''
      }
    },
    'formData.selectedSubjects'(value, oldValue) {
      const removed = oldValue.filter(subject => !value.includes(subject))
      removed.forEach(subject => {
        delete this.formData.subjectGrades[subject]
      })
    },
    'formData.selectedGiaSubjects'(value, oldValue) {
      const removed = oldValue.filter(subject => !value.includes(subject))
      removed.forEach(subject => {
        delete this.formData.giaResults[subject]
      })
    }
  },

  mounted() {
    window.addEventListener('online', this.setOnline)
    window.addEventListener('offline', this.setOffline)
    this.$el.addEventListener('input', this.scheduleRequiredHighlights, true)
    this.$el.addEventListener('change', this.scheduleRequiredHighlights, true)
    this.$el.addEventListener('blur', this.scheduleRequiredHighlights, true)

    if (!this.$model.isStaff && this.routeApplicationId) {
      this.resetPublicRouteToRoot()
    }

    if (this.$model.isStaff && this.applicationId) {
      this.loadApplicationById()
    }

    this.scheduleRequiredHighlights()
  },

  beforeUnmount() {
    window.removeEventListener('online', this.setOnline)
    window.removeEventListener('offline', this.setOffline)
    this.$el.removeEventListener('input', this.scheduleRequiredHighlights, true)
    this.$el.removeEventListener('change', this.scheduleRequiredHighlights, true)
    this.$el.removeEventListener('blur', this.scheduleRequiredHighlights, true)

    if (this.requiredHighlightFrame) {
      cancelAnimationFrame(this.requiredHighlightFrame)
    }
  },

  methods: {
    addYears,
    formatDateForInput,
    formatUnixDateTime,
    normalizeDigits,
    formatSnils,
    formatPhone,
    normalizePersonName,
    isPersonNameValid,
    isEmailValid,
    isPhoneComplete,
    isBlank,
    addRequiredIssue,
    addNameIssue,
    hasAddressData,
    addDateIssue,
    addGraduationYearIssue,
    setOnline() {
      this.offline = false
    },
    setOffline() {
      this.offline = true
    },
    updateSnils(value) {
      this.formData.snils = this.formatSnils(value)
    },
    updatePayerSnils(index, value) {
      const payer = this.formData.payers[index]

      if (!payer) {
        return
      }

      payer.snils = this.formatSnils(value)
    },
    updatePhone(value) {
      this.formData.phone = this.formatPhone(value)
    },
    updateApplicantName(field, value) {
      if (!['lastname', 'firstname', 'patronymic'].includes(field)) {
        return
      }

      this.formData[field] = this.normalizePersonName(value)
    },
    updatePayerName(index, field, value) {
      const payer = this.formData.payers[index]

      if (!payer || !['lastname', 'firstname', 'patronymic'].includes(field)) {
        return
      }

      payer[field] = this.normalizePersonName(value)
    },
    updatePayerPhone(index, value) {
      const payer = this.formData.payers[index]

      if (!payer) {
        return
      }

      payer.phone = this.formatPhone(value)
    },
    /**
     * Defers required-field highlighting until Vuetify has rendered the latest state.
     */
    scheduleRequiredHighlights() {
      if (this.requiredHighlightFrame) {
        cancelAnimationFrame(this.requiredHighlightFrame)
      }

      this.requiredHighlightFrame = requestAnimationFrame(() => {
        this.requiredHighlightFrame = null
        this.updateRequiredHighlights()
      })
    },
    /**
     * Highlights only empty required controls, using labels with required markers as source of truth.
     */
    updateRequiredHighlights() {
      if (!this.$el) {
        return
      }

      const candidates = Array.from(this.$el.querySelectorAll('.v-input, .address-form--required'))
        .filter(element =>
          element.classList.contains('address-form--required') ||
          element.querySelector('.statement-required, .passport-required')
        )

      candidates.forEach(element => {
        element.classList.toggle(
          'statement-required-empty',
          this.isRequiredControlEmpty(element)
        )
      })
    },
    /**
     * Returns true when a required control has no user-entered value.
     */
    isRequiredControlEmpty(element) {
      const controls = Array.from(element.querySelectorAll(
        'input:not([type="hidden"]):not([disabled]), ' +
        'textarea:not([disabled]), ' +
        'select:not([disabled])'
      ))

      if (!controls.length) {
        return false
      }

      const checkableControls = controls.filter(control =>
        control.type === 'checkbox' || control.type === 'radio'
      )

      if (checkableControls.length) {
        return !checkableControls.some(control => control.checked)
      }

      if (controls.some(control => !this.isBlank(control.value))) {
        return false
      }

      const selection = element.querySelector(
        '.v-select__selection-text, ' +
        '.v-autocomplete__selection-text, ' +
        '.v-chip__content'
      )

      return !selection || this.isBlank(selection.textContent)
    },
    getSubjectName(subjectId) {
      const subjects = this.$model && Array.isArray(this.$model.subjects)
        ? this.$model.subjects
        : []

      return getDictionaryName(subjects, subjectId)
    },
    getGiaSubjectName(subjectId) {
      const subjects = this.$model && Array.isArray(this.$model.subjectsGIA)
        ? this.$model.subjectsGIA
        : []

      return getDictionaryName(subjects, subjectId)
    },
    normalizeGraduationYear(value) {
      this.formData.graduationYear = this.normalizeDigits(value, 4)
    },
    updateSubjectGrade(subject, value) {
      this.formData.subjectGrades[subject] = this.normalizeDigits(value)
    },
    updateGiaResult(subject, value) {
      this.formData.giaResults[subject] = this.normalizeDigits(value)
    },
    updateCertificateAverage(value) {
      const normalized = String(value || '')
        .replace(',', '.')
        .replace(/[^\d.]/g, '')
        .replace(/(\..*)\./g, '$1')
        .replace(/^(\d+)(\.\d{0,2})?.*$/, '$1$2')

      this.formData.certificateAverage = normalized
    },
    normalizeCertificateAverage() {
      const value = String(this.formData.certificateAverage || '').replace(',', '.')

      if (value === '' || !/^\d+(\.\d{1,2})?$/.test(value)) {
        return
      }

      const average = Number(value)
      if (!Number.isFinite(average) || average < 0 || average > 5) {
        return
      }

      this.formData.certificateAverage = average.toFixed(2)
    },
    getDepartmentName(departmentId) {
      const department = this.$model.departments.find(
        item => String(item.id) === String(departmentId)
      )

      return department ? department.name : 'Филиал не указан'
    },
    /**
     * Checks whether a competitive group supports the selected target organization.
     */
    groupHasTarget(group, targetId) {
      return Object.values(group.target || {}).some(
        target => String(target.target_id) === String(targetId)
      )
    },
    /**
     * Returns available financing types for a competitive group.
     */
    getFundingOptions(group) {
      const options = []

      if (Number(group.budget) > 0) {
        options.push({
          value: 'budget',
          label: 'Бюджет'
        })
      }

      if (Number(group.contract) > 0) {
        options.push({
          value: 'contract',
          label: 'Договор'
        })
      }

      return options
    },
    getFundingLabel(financing) {
      const labels = {
        budget: 'Бюджет',
        contract: 'Договор'
      }

      return labels[financing] || financing
    },
    getEducationBaseLabel(educationBase) {
      const bases = this.$model && Array.isArray(this.$model.educationBases)
        ? this.$model.educationBases
        : []
      const base = bases.find(item => String(item.type_ed) === String(educationBase))

      return base ? (base.short_name || base.name || '') : ''
    },
    /**
     * Builds a stable contest key from group id and financing type.
     */
    getContestKey(group, financing) {
      return `${group.id}:${financing}`
    },
    getSpecialtyKey(group) {
      return [
        group.department_id || 0,
        group.base_education || '',
        group.code || '',
        group.name || ''
      ].join(':')
    },
    /**
     * Groups specialty contest variants by study form.
     */
    getSpecialtyFormOptions(groups) {
      const forms = new Map()

      groups.forEach(group => {
        const key = this.getGroupFormKey(group)

        if (!forms.has(key)) {
          forms.set(key, {
            key,
            name: group.study_name || 'Форма не указана',
            groups: []
          })
        }

        forms.get(key).groups.push(group)
      })

      return Array.from(forms.values()).sort((a, b) => a.name.localeCompare(b.name, 'ru'))
    },
    getGroupFormKey(group) {
      return String(group.code_fis || group.study_name || 'unknown')
    },
    /**
     * Returns financing options available inside a selected study form.
     */
    getFormFundingOptions(formOption) {
      const fundingValues = new Set()

      formOption.groups.forEach(group => {
        this.getFundingOptions(group).forEach(option => {
          fundingValues.add(option.value)
        })
      })

      return Array.from(fundingValues).map(value => ({
        value,
        label: this.getFundingLabel(value)
      }))
    },
    /**
     * Returns selectable contest options for a study form and financing type.
     */
    getContestOptionsForFunding(formOption, financing) {
      return formOption.groups
        .filter(group => Number(group[financing]) > 0)
        .flatMap(group => this.getContestOptions(group, financing))
    },
    getContestOptions(group, financing) {
      return [{
        key: this.getContestKey(group, financing),
        group,
        financing
      }]
    },
    getGroupDirectionLabel(group) {
      return group.dir_name || ''
    },
    /**
     * Builds labels for direction and qualifications inside contest options.
     */
    getContestOptionLabel(contestOption) {
      return [
        this.getGroupDirectionLabel(contestOption.group),
        this.getGroupQualificationLabel(contestOption.group)
      ].filter(Boolean).join(' · ')
    },
    /**
     * Lists all qualifications of a group without splitting one contest by them.
     */
    getGroupQualificationLabel(group) {
      return Object.values(group.qualification || {})
        .map(qualification => qualification.name)
        .filter(Boolean)
        .join(', ')
    },
    setSelectedSpecialtyForm(specialtyKey, formKey) {
      this.selectedSpecialtyFormKeys = {
        ...this.selectedSpecialtyFormKeys,
        [specialtyKey]: formKey
      }
    },
    /**
     * Finds the selected study form object for a specialty.
     */
    getSelectedSpecialtyForm(specialtyKey) {
      const formKey = this.selectedSpecialtyFormKeys[specialtyKey]

      if (!formKey) {
        return null
      }

      for (const department of this.groupedCompetitiveGroups) {
        const specialty = department.specialties.find(item => item.key === specialtyKey)
        if (!specialty) {
          continue
        }

        return specialty.formOptions.find(formOption => formOption.key === formKey) || null
      }

      return null
    },
    restoreSelectedContestControls() {
      const selectedFormKeys = {}

      this.formData.selectedContests = this.formData.selectedContests.map(contest => {
        const group = this.allCompetitiveGroups.find(
          item => String(item.id) === String(contest.groupId)
        )

        if (!group) {
          return contest
        }

        selectedFormKeys[this.getSpecialtyKey(group)] = this.getGroupFormKey(group)

        return {
          ...contest,
          educationBase: contest.educationBase || group.base_education
        }
      })

      this.selectedSpecialtyFormKeys = {
        ...this.selectedSpecialtyFormKeys,
        ...selectedFormKeys
      }
    },
    isContestSelected(contestOption) {
      return this.formData.selectedContests.some(
        contest => contest.key === contestOption.key
      )
    },
    isContestOptionDisabled(contestOption) {
      const selectedContest = this.formData.selectedContests.find(
        contest => contest.key === contestOption.key
      )

      if (selectedContest && selectedContest.confirmed) {
        return true
      }

      return !this.isContestSelected(contestOption) &&
        this.formData.selectedContests.length >= this.maxContestCount
    },
    /**
     * Adds or removes a contest while respecting the maximum priority count.
     */
    toggleContestSelection(contestOption, checked) {
      if (!checked) {
        this.removeContest(contestOption.key)
        return
      }

      if (this.isContestSelected(contestOption) ||
        this.formData.selectedContests.length >= this.maxContestCount) {
        return
      }

      this.formData.selectedContests.push({
        key: contestOption.key,
        groupId: contestOption.group.id,
        educationBase: contestOption.group.base_education,
        financing: contestOption.financing,
        isBudget: contestOption.financing === 'budget',
        priority: this.getNextContestPriority(),
        confirmed: false
      })
    },
    removeContest(key) {
      const contest = this.formData.selectedContests.find(
        item => item.key === key
      )

      if (contest && contest.confirmed) {
        return
      }

      this.formData.selectedContests = this.formData.selectedContests.filter(
        contest => contest.key !== key
      )
      this.normalizeContestPriorities()
    },
    /**
     * Moves the contest to the requested priority and recalculates all others.
     */
    updateContestPriority(key, priority) {
      const targetPriority = Number(priority)
      const contests = [...this.formData.selectedContests].sort(
        (a, b) => Number(a.priority) - Number(b.priority)
      )
      const targetIndex = contests.findIndex(contest => contest.key === key)

      if (targetIndex === -1 || !targetPriority) {
        return
      }

      const [target] = contests.splice(targetIndex, 1)
      contests.splice(targetPriority - 1, 0, target)

      this.formData.selectedContests = contests.map((contest, index) => ({
        ...contest,
        priority: index + 1
      }))
    },
    /**
     * Returns the first free priority number for a newly selected contest.
     */
    getNextContestPriority() {
      const used = this.formData.selectedContests
        .map(contest => Number(contest.priority))
        .filter(Boolean)

      const available = this.priorityOptions.find(priority => !used.includes(priority))

      return available || this.formData.selectedContests.length + 1
    },
    /**
     * Reorders priorities to a continuous 1..N sequence.
     */
    normalizeContestPriorities() {
      this.formData.selectedContests = [...this.formData.selectedContests]
        .sort((a, b) => Number(a.priority) - Number(b.priority))
        .map((contest, index) => ({
          ...contest,
          priority: index + 1
        }))
    },
    /**
     * Clears removable contest choices when identifying filters change.
     */
    clearContestSelection() {
      this.formData.selectedContests = this.formData.selectedContests.filter(
        contest => contest.confirmed
      )
      this.normalizeContestPriorities()
      this.selectedSpecialtyFormKeys = {}
    },
    addPayer() {
      this.formData.payers.push(defaultPayer())
    },
    removePayer(index) {
      if (this.formData.payers.length <= 1) {
        return
      }

      this.formData.payers.splice(index, 1)
    },
    hasRequiredName() {
      return Boolean(
        this.formData.lastname.trim() &&
        this.formData.firstname.trim()
      )
    },
    openLoadApplicationDialog() {
      this.loadApplicationPassword = ''
      this.loadApplicationErrors = []
      this.loadApplicationDialog = true
    },
    /**
     * Loads an application by id for staff users.
     */
    loadApplicationById() {
      return this.loadApplicationRequest({
        id: this.applicationId
      })
    },
    /**
     * Loads a public application after the applicant enters the edit password.
     */
    loadApplicationByPassword() {
      if (!this.loadApplicationPassword.trim()) {
        this.loadApplicationErrors = ['Укажите пароль анкеты']
        return Promise.resolve(null)
      }

      return this.loadApplicationRequest({
        id: this.routeApplicationId,
        password: this.loadApplicationPassword
      })
    },
    /**
     * Sends the application load request and handles access errors.
     */
    loadApplicationRequest({ id = null, password = '' } = {}) {
      if (this.ApplicationLoading) {
        return Promise.resolve(null)
      }

      this.ApplicationLoading = true
      this.loadApplicationErrors = []

      const requestData = new FormData()
      if (id) {
        requestData.append('id', id)
      }
      if (password) {
        requestData.append('password', password)
      }

      return this.axios
        .post(
          this.$model.wwwroot +
          "/procollege/manage_groups/admission/createApplicant/loadApplication",
          requestData
        )
        .then((response) => {
          const data = response.data || {}
          const errors = Array.isArray(data.errors) ? data.errors : []

          if (errors.length) {
            this.loadApplicationErrors = errors
            return null
          }

          this.applyLoadedApplication(data)
          return data
        })
        .catch((error) => {
          const data = error.response && error.response.data
            ? error.response.data
            : {}

          this.loadApplicationErrors = Array.isArray(data.errors)
            ? data.errors
            : ['Не удалось загрузить анкету']

          return null
        })
        .finally(() => {
          this.ApplicationLoading = false
        })
    },
    /**
     * Applies loaded backend data without triggering contest reset watchers.
     */
    applyLoadedApplication(data) {
      const id = this.normalizeApplicationId(data.id || data.applicationId)

      this.isApplyingLoadedApplication = true
      this.formData = this.mergeFormData(defaultForm(), data.formData || {})
      this.formData.acceptedDocuments = normalizeAcceptedDocuments(
        this.formData.acceptedDocuments
      )
      this.applicationId = id || null
      this.applicationAccessGranted = Boolean(id)
      this.applicationEditPassword = this.$model.isStaff ? '' : this.loadApplicationPassword
      this.restoreSelectedContestControls()
      this.openSelectedPanelsToken += 1
      this.ApplicationErrors = []
      this.ApplicationErrorLinks = []
      this.ApplicationWarnings = []
      this.loadApplicationDialog = false
      this.loadApplicationPassword = ''
      this.loadApplicationErrors = []

      if (id) {
        this.replaceRouteWithApplicationId(id)
      }

      this.$nextTick(() => {
        this.isApplyingLoadedApplication = false
      })
    },
    /**
     * Merges loaded partial form data into the current full default model.
     */
    mergeFormData(base, loaded) {
      const result = { ...base }

      Object.keys(loaded).forEach((key) => {
        if (
          loaded[key] &&
          typeof loaded[key] === 'object' &&
          !Array.isArray(loaded[key]) &&
          base[key] &&
          typeof base[key] === 'object' &&
          !Array.isArray(base[key])
        ) {
          result[key] = this.mergeFormData(base[key], loaded[key])
        } else {
          result[key] = loaded[key]
        }
      })

      return result
    },
    /**
     * Sends the explicit save request as FormData for Moodle param handling.
     */
    saveApplicationRequest({ draft = false } = {}) {
      if (this.showConsentGate) {
        return Promise.resolve(null)
      }

      if (this.isPublicApprovedApplication) {
        this.ApplicationErrors = ['Анкета уже утверждена приемной комиссией. Редактирование недоступно.']
        this.ApplicationErrorLinks = []
        this.ApplicationWarnings = []
        return Promise.resolve(null)
      }

      if (this.isAdmissionPeriodClosed) {
        this.ApplicationErrors = this.admissionPeriodMessage
          ? [this.admissionPeriodMessage]
          : []
        this.ApplicationErrorLinks = []
        this.ApplicationWarnings = []
        return Promise.resolve(null)
      }

      if (!this.hasRequiredName()) {
        return Promise.resolve(null)
      }

      if (this.ApplicationSaving) {
        return Promise.resolve(null)
      }

      this.ApplicationSaving = true
      this.ApplicationErrorLinks = []
      this.ApplicationWarnings = []

      const formData = new FormData()
      if (this.applicationId) {
        formData.append('id', this.applicationId)
      }
      if (!this.$model.isStaff && this.applicationId && this.applicationEditPassword) {
        formData.append('password', this.applicationEditPassword)
      }
      formData.append('formData', JSON.stringify(this.formData))
      formData.append('draft', draft ? '1' : '0')

      return this.axios
        .post(
          this.$model.wwwroot +
          "/procollege/manage_groups/admission/createApplicant/saveApplication",
          formData
        )
        .then((response) => {
          const data = response.data || {}
          const id = this.normalizeApplicationId(data.id || data.applicationId)

          if (id) {
            this.applicationId = id
            this.replaceRouteWithApplicationId(id)
          }

          if (data.password) {
            this.applicationPassword = data.password
            this.applicationEditPassword = data.password
            this.passwordDialog = true
          }

          if (Array.isArray(data.selectedContests)) {
            this.formData.selectedContests = data.selectedContests
            this.restoreSelectedContestControls()
          }

          this.ApplicationErrors = Array.isArray(data.errors) ? data.errors : []
          this.ApplicationErrorLinks = Array.isArray(data.errorLinks) ? data.errorLinks : []
          this.ApplicationWarnings = Array.isArray(data.warnings) ? data.warnings : []

          return data
        })
        .catch((error) => {
          const data = error.response && error.response.data
            ? error.response.data
            : {}

          this.ApplicationErrors = Array.isArray(data.errors)
            ? data.errors
            : [draft ? 'Не удалось сохранить черновик' : 'Не удалось сохранить анкету']
          this.ApplicationErrorLinks = []
          this.ApplicationWarnings = []

          return null
        })
        .finally(() => {
          this.ApplicationSaving = false
        })
    },
    /**
     * Updates the hash route after backend returns a persistent application id.
     */
    replaceRouteWithApplicationId(id) {
      const routeId = this.normalizeApplicationId(id)
      const currentId = this.$route.params.id

      if (!routeId || String(currentId || '') === routeId) {
        return
      }

      this.$router.replace({
        path: `/${routeId}`
      })
    },
    /**
     * Hides direct public ids until the applicant authenticates by password.
     */
    resetPublicRouteToRoot() {
      this.routeApplicationId = null
      this.applicationId = null
      this.applicationAccessGranted = false

      if (this.$route.params.id) {
        this.$router.replace({
          path: '/'
        })
      }
    },
    /**
     * Removes accidental duplicated slashes from route/application ids.
     */
    normalizeApplicationId(id) {
      return String(id || '').replace(/^\/+/, '')
    },
    /**
     * Toggles the staff approval flag without running full form validation.
     */
    toggleApplicationApproval() {
      if (!this.$model.isStaff || !this.applicationId || this.approvalSaving) {
        return
      }

      this.approvalSaving = true
      this.ApplicationErrors = []
      this.ApplicationErrorLinks = []

      const requestData = new FormData()
      requestData.append('id', this.applicationId)
      requestData.append('approved', this.formData.approved ? '0' : '1')

      this.axios
        .post(
          this.$model.wwwroot +
          "/procollege/manage_groups/admission/createApplicant/setApplicationApproval",
          requestData
        )
        .then((response) => {
          const data = response.data || {}
          const errors = Array.isArray(data.errors) ? data.errors : []

          if (errors.length) {
            this.ApplicationErrors = errors
            this.ApplicationErrorLinks = Array.isArray(data.errorLinks) ? data.errorLinks : []
            return
          }

          this.formData.approved = Boolean(data.approved)
          this.ApplicationErrorLinks = []
        })
        .catch((error) => {
          const data = error.response && error.response.data
            ? error.response.data
            : {}

          this.ApplicationErrors = Array.isArray(data.errors)
            ? data.errors
            : ['Не удалось изменить статус заявления']
          this.ApplicationErrorLinks = []
        })
        .finally(() => {
          this.approvalSaving = false
        })
    },
    /**
     * Toggles the staff flag for accepted original documents.
     */
    toggleApplicationOriginals() {
      if (!this.$model.isStaff || !this.applicationId || this.originalsSaving) {
        return
      }

      this.originalsSaving = true
      this.ApplicationErrors = []
      this.ApplicationErrorLinks = []

      const requestData = new FormData()
      requestData.append('id', this.applicationId)
      requestData.append('orig', this.formData.originalsAccepted ? '0' : '1')

      this.axios
        .post(
          this.$model.wwwroot +
          "/procollege/manage_groups/admission/createApplicant/setApplicationOriginals",
          requestData
        )
        .then((response) => {
          const data = response.data || {}
          const errors = Array.isArray(data.errors) ? data.errors : []

          if (errors.length) {
            this.ApplicationErrors = errors
            return
          }

          this.formData.originalsAccepted = Boolean(data.orig)
        })
        .catch((error) => {
          const data = error.response && error.response.data
            ? error.response.data
            : {}

          this.ApplicationErrors = Array.isArray(data.errors)
            ? data.errors
            : ['Не удалось изменить статус оригиналов']
        })
        .finally(() => {
          this.originalsSaving = false
        })
    },
    /**
     * Opens the administrator audit dialog and loads the latest applicant events.
     */
    openApplicantLogDialog() {
      if (!this.$model.isAdmin || !this.applicationId) {
        return
      }

      this.applicantLogDialog = true
    },
    scrollToSection(sectionId) {
      const section = document.getElementById(sectionId)

      if (!section) {
        return
      }

      section.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    },
    /**
     * Records consent acceptance for the current page session.
     */
    acceptConsent() {
      this.consentAccepted = true
    },
    /**
     * Extracts messages returned by Vuetify form validation.
     */
    collectVuetifyValidationErrors(validation) {
      const errors = []

      ;(validation.errors || []).forEach(item => {
        ;(item.errorMessages || []).forEach(message => {
          if (message) {
            errors.push(message)
          }
        })
      })

      return [...new Set(errors)]
    },
    /**
     * Shows client-side validation errors and moves the user to the problem area.
     */
    showClientValidationErrors(errors) {
      const normalizedErrors = [...new Set(errors.filter(Boolean))]

      this.ApplicationErrors = normalizedErrors.length
        ? normalizedErrors
        : ['Проверьте обязательные поля формы']
      this.ApplicationErrorLinks = []
      this.ApplicationWarnings = []

      this.$nextTick(() => {
        const target = document.querySelector('.statement-validation-summary') ||
          document.querySelector('.statement-Application-errors') ||
          document.querySelector('.v-input--error')

        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })
        }
      })
    },
    /**
     * Runs Vuetify validation and sends the final application save request.
     */
    async submitApplication() {
      if (this.showConsentGate) {
        return
      }

      if (this.isPublicApprovedApplication) {
        this.ApplicationErrors = ['Анкета уже утверждена приемной комиссией. Редактирование недоступно.']
        this.ApplicationErrorLinks = []
        this.ApplicationWarnings = []
        return
      }

      if (this.isAdmissionPeriodClosed) {
        this.ApplicationErrors = this.admissionPeriodMessage
          ? [this.admissionPeriodMessage]
          : []
        this.ApplicationErrorLinks = []
        this.ApplicationWarnings = []
        return
      }

      const validation = await this.$refs.statementForm.validate()
      const formIssues = this.formIssueList
      if (!validation.valid ) {
        this.showClientValidationErrors([
          ...formIssues,
          ...this.collectVuetifyValidationErrors(validation)
        ])
        return
      }

      //исключение для заявлений, поданых через епгу
      if (formIssues.length && !this.formData.submittedViaEpgu) {
        this.showClientValidationErrors(formIssues)
        return
      }

      const data = await this.saveApplicationRequest({ draft: false })

      if (!data || (Array.isArray(data.errors) && data.errors.length)) {
        return
      }

      this.$nextTick(() => {
        this.$emit('saved', { ...this.formData })
      })
    }
  }
}
</script>

<style>
.statement-form {
  max-width: 1180px;
  padding: 24px 16px;
  margin: 0 auto;
}

.statement-card {
  overflow: visible;
  border: 1px solid #e6e8ee;
  border-radius: 8px;
}

.statement-header {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 28px 20px;
  background: #f7f9fc;
  border-bottom: 1px solid #e6e8ee;
  border-radius: 8px 8px 0 0;
}

.statement-header>div:first-child {
  min-width: 0;
}

.statement-header__aside {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 10px;
  align-items: flex-end;
}

.statement-existing-link {
  font-size: 14px;
  line-height: 1.35;
  color: #1d4ed8;
  text-align: right;
  text-decoration: none;
}

.statement-existing-link:hover {
  text-decoration: underline;
}

.statement-title {
  display: block;
  padding: 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.25;
  color: #1f2937;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: normal;
}

.statement-title__institution,
.statement-title__form {
  display: block;
  max-width: 100%;
}

.statement-title__form {
  margin-top: 6px;
  font-size: 18px;
  font-weight: 600;
  color: #344054;
}

.statement-offline-chip {
  flex: 0 0 auto;
}

.statement-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px 28px;
  background: #ffffff;
}

.statement-layout {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 22px;
  align-items: start;
}

.statement-nav {
  position: sticky;
  top: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;
  background: #f8fafc;
  border: 1px solid #e6e8ee;
  border-radius: 8px;
}

.statement-nav__link {
  display: grid;
  grid-template-columns: 26px minmax(0, 1fr);
  gap: 8px;
  align-items: center;
  width: 100%;
  padding: 8px;
  font: inherit;
  color: #344054;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: 6px;
}

.statement-nav__link:hover {
  color: #1d4ed8;
  background: #eff6ff;
}

.statement-nav__index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  font-size: 12px;
  font-weight: 700;
  color: #1d4ed8;
  background: #dbeafe;
  border-radius: 50%;
}

.statement-nav__text {
  min-width: 0;
  overflow-wrap: anywhere;
}

.statement-nav__admin {
  padding-top: 10px;
  margin-top: 6px;
  border-top: 1px solid #e6e8ee;
}

.statement-nav__admin :deep(.v-btn) {
  cursor: pointer;
}

.statement-sections {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}

.statement-anchor {
  scroll-margin-top: 16px;
}

.statement-period-alert {
  margin-bottom: 0;
}

.statement-loading {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.statement-validation-summary {
  margin-bottom: 0;
}

.statement-validation-summary__title {
  margin-bottom: 8px;
  font-weight: 700;
}

.statement-validation-summary__list {
  margin: 0;
  padding-left: 20px;
}

.statement-validation-summary__list li+li {
  margin-top: 4px;
}

.statement-section {
  padding: 22px;
  background: #ffffff;
  border: 1px solid #e6e8ee;
  border-radius: 8px;
}

.statement-section__header {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 20px;
}

.statement-section__marker {
  display: inline-flex;
  flex: 0 0 32px;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  font-weight: 700;
  color: #ffffff;
  background: #2563eb;
  border-radius: 50%;
}

.statement-section__title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
  color: #1f2937;
}

.statement-section__description {
  margin: 4px 0 0;
  color: #667085;
}

.statement-fields {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;
}

.statement-inline-fields {
  display: grid;
  gap: 12px;
}

.statement-inline-fields--personal {
  grid-template-columns: minmax(180px, 1fr) minmax(180px, 1fr);
}

.statement-radio-group {
  margin-top: 0;
}

.statement-required {
  color: #d92d20;
  font-weight: 700;
}

.statement-form .statement-required-empty .v-field {
  background-color: #fff1f2;
}

.statement-form .statement-required-empty .v-field__overlay {
  background-color: #fff1f2;
}

.statement-subsection {
  padding: 14px;
  border: 1px solid #e6e8ee;
  border-radius: 8px;
  background: #f9fafb;
}

.statement-subsection__title {
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 700;
  color: #344054;
}

.statement-date-field input[type="date"] {
  position: relative;
  padding-right: 44px;
}

.statement-date-field input[type="date"]::-webkit-calendar-picker-indicator {
  position: absolute;
  right: 10px;
  width: 20px;
  height: 20px;
  margin: 0;
  cursor: pointer;
}

.statement-empty {
  padding: 18px;
  color: #667085;
  background: #f7f9fc;
  border: 1px dashed #cfd6e4;
  border-radius: 8px;
}

.statement-muted {
  color: #667085;
  font-weight: 400;
}

.statement-actions {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  padding: 18px 28px 24px;
  border-top: 1px solid #e6e8ee;
}

.statement-actions__buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}

.statement-actions--end {
  justify-content: flex-end;
}

.statement-actions--form {
  flex-direction: column;
  align-items: stretch;
}

.statement-actions--form .statement-actions__buttons {
  width: 100%;
}

.statement-application-warnings {
  width: 100%;
}

.statement-offline-note {
  color: #b42318;
}

.statement-Application-errors {
  color: #b42318;
  font-size: 14px;
  line-height: 1.4;
}

.statement-password {
  padding: 12px 14px;
  margin-top: 14px;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0;
  color: #1f2937;
  text-align: center;
  background: #f7f9fc;
  border: 1px solid #e6e8ee;
  border-radius: 8px;
}

.statement-save-information {
  padding-top: 14px;
  margin-top: 16px;
  color: #344054;
  line-height: 1.5;
  border-top: 1px solid #e6e8ee;
}

.statement-save-information a {
  color: #0b57d0;
  font-weight: 600;
}

.statement-consent {
  color: #344054;
  line-height: 1.55;
}

.statement-consent :deep(p) {
  margin: 0 0 12px;
}

@media (max-width: 640px) {
  .statement-form {
    padding: 12px;
  }

  .statement-header,
  .statement-content,
  .statement-actions {
    padding-right: 16px;
    padding-left: 16px;
  }

  .statement-header,
  .statement-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .statement-header__aside {
    align-items: flex-start;
  }

  .statement-existing-link {
    text-align: left;
  }

  .statement-section {
    padding: 16px;
  }

  .statement-inline-fields--personal {
    grid-template-columns: minmax(0, 1fr);
  }

  .statement-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .statement-nav {
    position: static;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .statement-nav__admin {
    grid-column: 1 / -1;
  }

  .statement-actions__buttons {
    justify-content: stretch;
  }

  .statement-actions__buttons :deep(.v-btn) {
    flex: 1 1 100%;
  }
}
</style>
